// #336 — a new dashboard build announces itself. The production image writes /version.json with a build id and bakes
// the same id into the bundle (VITE_BUILD_ID, dashboard/Dockerfile); nginx serves /version.json with no-cache. An open
// tab runs its bundle until the user reloads, so it checks the file at start, every 10 minutes while visible and when
// it comes to the front (at most once a minute). A different id raises one persistent toast with an Update button, once per new
// build; it never reloads by itself (owner's pick). The Vite dev server (stage) has no build id: nothing runs there.
import { toast } from 'sonner'
import i18next from 'i18next'
import { liveLog } from '@/lib/liveLink'

export const BUILD_CHECK_INTERVAL_MS = 600_000
/** A tab coming to the front checks again only this long after the last check. */
export const BUILD_VISIBLE_CHECK_GAP_MS = 60_000
const BUILD_TOAST_ID = 'build-new'

export interface BuildWatchOptions {
  /** The build id baked into this bundle. */
  current: string
  fetchJson: (url: string, init?: RequestInit) => Promise<unknown>
  /** Called once per build id that differs from `current`. */
  onNewBuild: (build: string) => void
  intervalMs?: number
}

/** Watch /version.json; returns the function that stops the timer and the listener. A failed fetch or an answer
 *  without a string `build` is silent (one `[live] build` diagnostics line). */
export function startBuildWatch({
  current, fetchJson, onNewBuild, intervalMs = BUILD_CHECK_INTERVAL_MS,
}: BuildWatchOptions): () => void {
  let stopped = false
  let lastCheckAt = 0
  const announced = new Set<string>()

  const check = async () => {
    lastCheckAt = Date.now()
    try {
      const answer = await fetchJson('/version.json', { cache: 'no-store' })
      if (stopped) return
      const build = (answer as { build?: unknown } | null | undefined)?.build
      if (typeof build !== 'string' || build === '' || build === current || announced.has(build)) return
      announced.add(build)
      onNewBuild(build)
    } catch (error) {
      liveLog('build', { error: String(error) })
    }
  }

  const onVisibility = () => {
    if (stopped || document.visibilityState !== 'visible') return
    if (Date.now() - lastCheckAt < BUILD_VISIBLE_CHECK_GAP_MS) return
    void check()
  }

  void check()
  // Fix round 1 (review M4) — a hidden tab sends nothing; the visible handler checks when it comes back.
  const timer = setInterval(() => { if (document.visibilityState !== 'hidden') void check() }, intervalMs)
  document.addEventListener('visibilitychange', onVisibility)
  return () => {
    stopped = true
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}

/** GET a JSON file; a non-2xx answer (no version.json: 404) rejects. */
export async function fetchVersionJson(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(url, init)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return response.json()
}

/** One persistent toast (never auto-dismissed; the same id replaces it, never stacks): "A new version of the dashboard
 *  is available" with an Update button that reloads the page. The user can close it. The English default covers a
 *  check that answers before the notifications strings have loaded (the first check runs as the page starts). */
export function showNewBuildToast(reload: () => void = () => window.location.reload()): void {
  toast.info(i18next.t('build.newVersion', { ns: 'notifications', defaultValue: 'A new version of the dashboard is available' }), {
    id: BUILD_TOAST_ID,
    duration: Infinity,
    action: {
      label: i18next.t('build.update', { ns: 'notifications', defaultValue: 'Update' }),
      onClick: () => reload(),
    },
  })
}
